import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useGate } from '../context/GateContext';
import { useAuth } from '../context/AuthContext';
import EntryCard from '../components/EntryCard';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { SECTIONS } from '../lib/utils';

const { FiArrowRight, FiHeart, FiCamera, FiGlobe, FiUsers, FiLock } = FiIcons;

const Home = () => {
  const [latestEntries, setLatestEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const { access } = useGate();
  const { user } = useAuth();

  useEffect(() => {
    fetchLatest();
  }, [access, user]);

  const fetchLatest = async () => {
    // Determine allowed sections: All if Admin, otherwise based on Gate access
    const allowedSections = user 
      ? Object.keys(SECTIONS) 
      : Object.keys(access).filter(k => access[k]);

    if (allowedSections.length === 0) {
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('entries_20240520')
      .select('*')
      .in('section', allowedSections)
      .eq('status', 'published')
      .order('date', { ascending: false })
      .limit(4);

    if (!error) setLatestEntries(data);
    setLoading(false);
  };

  const navCards = [
    { id: 'luka', icon: FiHeart, desc: 'Growing up fast' },
    { id: 'klara', icon: FiCamera, desc: 'Daily wonders' },
    { id: 'family', icon: FiUsers, desc: 'Shared moments' },
    { id: 'trips', icon: FiGlobe, desc: 'Around the world' },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 py-12">
      <div className="text-center mb-16">
        <h1 className="text-4xl md:text-6xl font-serif font-bold mb-6 tracking-tight">
          Capturing the <span className="italic text-[#8c7851] underline decoration-1 underline-offset-8">Everyday</span>
        </h1>
        <p className="text-lg text-stone-500 max-w-2xl mx-auto leading-relaxed">
          A timeless digital heirloom for our family. Private, secure, and built to last.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20">
        {navCards.map((card) => (
          <Link key={card.id} to={`/section/${card.id}`} className="group p-6 bg-white rounded-3xl border border-stone-200 hover:border-[#8c7851] hover:shadow-lg transition-all text-center">
            <div className="w-12 h-12 bg-stone-50 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-[#8c7851] group-hover:text-white transition-colors">
              <SafeIcon icon={card.icon} className="w-6 h-6" />
            </div>
            <h3 className="font-serif font-bold text-lg capitalize">{card.id}</h3>
            <p className="text-xs text-stone-400 mt-1">{card.desc}</p>
          </Link>
        ))}
      </div>

      <section>
        <div className="flex justify-between items-end mb-8">
          <div>
            <h2 className="text-2xl font-serif font-bold text-[#2c2c2c]">Latest Memories</h2>
            <p className="text-sm text-stone-500">The most recent updates from the archive.</p>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[1, 2].map(i => <div key={i} className="h-64 bg-stone-50 rounded-2xl animate-pulse" />)}
          </div>
        ) : latestEntries.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {latestEntries.map(entry => (
              <EntryCard key={entry.id} entry={entry} />
            ))}
          </div>
        ) : (
          <div className="bg-stone-50 rounded-3xl p-12 text-center border-2 border-dashed border-stone-200">
            <SafeIcon icon={FiLock} className="mx-auto w-10 h-10 text-stone-300 mb-4" />
            <h3 className="font-serif font-bold text-xl mb-2">Private Archive</h3>
            <p className="text-stone-500">Unlock a section above to view the latest memories.</p>
          </div>
        )}
      </section>
    </div>
  );
};

export default Home;